import { Injectable } from '@angular/core';
import { FunctionLifeCycle } from '../telephonyutils';
import { MessagingProvider } from '../telephony.provider';
import { Client, Conversation, Message, MessageUpdateReason, Participant } from '@twilio/conversations';
import { lastValueFrom } from 'rxjs';
import { MessengerService } from '../messenger.service';
import { FormattedEmail } from '@app/core/helpers';
import { AuthenticationService, Logger } from '@app/core';
import { ToastService } from '../../../services/toast/toast.service';
const log = new Logger('TwilioMessagingProvider');
@Injectable({ providedIn: 'root' })
export class TwilioMessagingProvider extends MessagingProvider {
  private callerId = '';
  private primaryCampaign: any = {};
  private client: any;
  private twilio_settings: any;
  private connectionStatus: string = 'Disconnected';
  private conversations: Conversation[] = [];
  private userName = '';
  constructor(
    private messengerService: MessengerService,
    private authService: AuthenticationService,
    private toast: ToastService
  ) {
    super();
    this.userName = FormattedEmail(this.authService?.authData?.Username, false);
    this.authService.onlogout.subscribe((x: any) => {
      this.destroy({ onStart: () => { }, onEnd: () => { } });
    });
  }

  async init(lf: FunctionLifeCycle | null) {
    lf?.onStart();
    //this.setCampaign(campaign);
    await this.fetchTwilioSettings();
    this.userName = FormattedEmail(this.authService?.authData?.Username, false);
    this.setupChat();
    lf?.onEnd();
  }
  async fetchTwilioSettings() {
    log.info('Fetching twilio chat settings');
    const twilio_settings = localStorage.getItem('twilio_chat_settings');
    if (twilio_settings == null) {
      const settings = await lastValueFrom(this.messengerService.getTwilioSettings());
      this.twilio_settings = settings;
      localStorage.setItem('twilio_chat_settings', JSON.stringify(settings));
    } else {
      this.twilio_settings = JSON.parse(twilio_settings);
    }
    this.events.next({ name: 'Settings', type: 'get', data: this.twilio_settings });
    return this.twilio_settings;
  }

  async setupChat() {
    const response: any = await lastValueFrom(
      this.messengerService.getToken(
        FormattedEmail(this.authService.authData.Username, false),
        this.twilio_settings.AccountSid
      )
    );
    this.client = new Client(response.Token);
    this.setupClientListeners();
    this.clientInitialized = true;
  }
  public setCampaign(campaign: any) {
    this.callerId = campaign.CallerId;
    this.primaryCampaign = campaign;
  }
  private setupClientListeners() {
    this.client.on('stateChanged', (state: any) => {
      console.log(new Date().toLocaleString() + '  ➤  Chat state changed to => ' + state);
      if (state === 'initialized') {
        this.connectionStatus = 'Connected';
      } else {
        this.connectionStatus = state;
      }
      this.events.next({ name: 'Client', type: 'status', data: this.connectionStatus });
    });
    this.client.on('conversationJoined', (conversation: Conversation) => {
      console.log(new Date().toLocaleString() + '  ➤  Chat conversationJoined event => ', conversation);
      const exist = this.conversations.find((x: Conversation) => x.sid == conversation.sid);
      if (!exist) {
        this.conversations.push(conversation);
        this.setupConversationEvents(conversation);
      }
      this.events.next({ name: 'Conversation', type: 'joined', data: conversation });
    });
    this.client.on('conversationLeft', (conversation: Conversation) => {
      console.log(new Date().toLocaleString() + '  ➤  Chat conversationLeft event => ');
      const index = this.conversations.findIndex((x) => x.sid == conversation.sid);
      if (index > -1) {
        conversation.removeAllListeners();
        this.conversations.splice(index, 1);
        this.events.next({ name: 'Conversation', type: 'left', data: conversation.sid });
      }
    });
    this.client.on('tokenAboutToExpire', async () => {
      const response: any = await lastValueFrom(
        this.messengerService.getToken(this.userName, this.twilio_settings.AccountSid)
      );
      await this.client.updateToken(response.token);
    });
    this.client.on('tokenExpired', () => {
      console.log(new Date().toLocaleString() + '  ➤  Chat tokenExpired event => ');
    });
    this.client.on('connectionError', (conn: any) => {
      console.log(new Date().toLocaleString() + '  ➤  Chat connectionError event => ', conn);
    });
    this.client.on('participantLeft', (state: any) => {
      console.log(new Date().toLocaleString() + '  ➤  participantLeft', state);
      const index = this.conversations.findIndex((x) => x.sid == state.conversation.sid);
      if (index > -1) {
        this.conversations[index].removeAllListeners();
        this.conversations.splice(index, 1);
        this.events.next({ name: 'Participant', type: 'left', data: state.conversation.sid });
      }
    });
  }
  setupConversationEvents(conversation: Conversation) {
   
    conversation.on('messageAdded', (message: Message) => {
      console.log(`Message added of conversation sid: ${message.conversation.sid}`);
      this.events.next({ name: 'Message', type: 'added', data: message });
    });
    conversation.on('messageUpdated', (updates: any) => {
      this.events.next({ name: 'Message', type: 'updated', data: updates.message });
    });
    conversation.on('updated', (m: any) => {
      console.log(new Date().toLocaleString() + '  ➤  conversation update', m);
      if (m.updateReasons.includes('attributes')) {
        // Access the new attributes via conversation.attributes
        const newAttributes: any = conversation.attributes;
        // Update your application UI or state here
        const attributes = this.getType(newAttributes) == 'string' ? JSON.parse(newAttributes) : newAttributes;
        if (attributes.tcpa) {
          this.events.next({
            name: 'Conversation',
            type: 'tcpa-consent',
            data: { sid: conversation.sid, value: attributes.tcpa },
          });
        }
      }
      else if (m.conversation.attributes?.tcpa) {
        this.events.next({
          name: 'Conversation',
          type: 'tcpa-consent',
          data: { sid: conversation.sid, value: m.conversation.attributes?.tcpa },
        });
      }
    });
    conversation.on('typingStarted', (participant: Participant) => {
      this.events.next({
        name: 'Conversation',
        type: 'typing',
        data: { typing: { on: true, user: participant.identity }, sid: conversation.sid },
      });
    });
    conversation.on('typingEnded', (participant: Participant) => {
      this.events.next({
        name: 'Conversation',
        type: 'typing',
        data: { typing: { on: false, user: '' }, sid: conversation.sid },
      });
    });
    setTimeout(async () => {
      const participants = await conversation.getParticipants();
      const customer = participants.filter((x) => x.identity != this.userName);
      customer.forEach(async (c) => {
        if (c.type !== 'sms' && c.type !== 'whatsapp') {
          const user = await c.getUser();
          if (user) {
            this.events.next({
              name: 'Conversation',
              type: 'online',
              data: { online: user.isOnline, sid: conversation.sid },
            });
            user.on('updated', (state: any) => {
              console.log(new Date().toLocaleString() + '  ➤  user updated', state);
              this.events.next({
                name: 'Conversation',
                type: 'online',
                data: { online: user.isOnline, sid: conversation.sid },
              });
            });
          }
        }
      });
    }, 2000);
  }
  getType(variable: any) {
    return Object.prototype.toString.call(variable).slice(8, -1).toLowerCase();
  }
  setAllMessagesRead(lifecyle: FunctionLifeCycle, sid: string) {
    const cconv = this.conversations.find((x) => x.sid == sid);
    if (cconv) {
      cconv.setAllMessagesRead().then((r) => console.log(r));
    }
  }
  async sendMessage(lifecyle: FunctionLifeCycle, sid: string, payload: any) {
    if (this.connectionStatus.toLowerCase() !== 'connected') {
      this.toast.warning('Chat System is not connected. Please check your internet and try again');
      return;
    }
    const currentChannel = this.conversations.find((x) => x.sid == sid);
    if (currentChannel) {
      if (typeof payload == 'string') {
        currentChannel.sendMessage(payload);
      } else {
        const pipeline = await currentChannel.prepareMessage();
        const medias = payload;
        for (let i = 0; i < medias.length; i++) {
          pipeline.addMedia(medias[i]);
        }
        pipeline.build().send();
      }
    } else {
      console.log(new Date().toLocaleString() + '  ➤  No active conv found');
    }
  }
  async sendTCPAMessage(lifecyle: FunctionLifeCycle, sid: string, payload: any) {
    if (this.connectionStatus.toLowerCase() !== 'connected') {
      this.toast.warning('Chat System is not connected. Please check your internet and try again');
      return;
    }
    const currentChannel: any = this.conversations.find((x) => x.sid == sid);
    if (currentChannel) {

      const newAttributes: any = currentChannel.attributes;
      const attributes = this.getType(newAttributes) == 'string' ? JSON.parse(newAttributes) : newAttributes;


      if (!attributes.tcpa) {
        attributes.tcpa = {
          consentText: payload,
          userResponse: null,
          timestamp: new Date().toISOString(),
          consentVersion: '1.0',
          consentAskCount: 1,
          lastMessageIndex: currentChannel.lastMessage.index,
        }
      }
      else {

        const lastIndex = currentChannel.lastMessage.index;
        if (attributes.tcpa.lastMessageIndex == lastIndex) {
          return false;
        }
        if (attributes.tcpa.userResponse) {
          return false;
        }

        attributes.tcpa.consentAskCount = attributes.tcpa.consentAskCount + 1;
        attributes.tcpa.consentText = payload;
        attributes.tcpa.timestamp = new Date().toISOString();
        attributes.tcpa.lastMessageIndex = currentChannel.lastMessage.index;
      }
      currentChannel.updateAttributes(attributes).then((x: any) => console.log(new Date().toLocaleString() + '  ➤  TCPA message sent successfully')).catch((e: any) => console.log(new Date().toLocaleString() + '  ➤  Failed to send TCPA message', e));
      return true;
    } else {
      console.log(new Date().toLocaleString() + '  ➤  No active conv found');
      return false;
    }

  }

  async typing(lifecyle: FunctionLifeCycle, sid: string) {
    const currentChannel = this.conversations.find((x) => x.sid == sid);
    if (currentChannel) {
      currentChannel.typing();
    }
  }
  destroy(lifecyle: FunctionLifeCycle) {
    if (this.client) {
      this.client.removeAllListeners();
      this.client.shutdown();
    }
    this.conversations.forEach(async (channel) => {
      await channel.removeAllListeners();
    });
    this.client = null;
    this.conversations = [];
  }
  async getParticipantByIdentity(lifecyle: FunctionLifeCycle, sid: string) {
    const cconv = this.conversations.find((x) => x.sid == sid);
    if (cconv) {
      const participant = await cconv.getParticipantByIdentity(this.userName);
      return participant;
    }
    return null;
  }
}
